import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { apiKeyService } from "@/services/apiKeyService";
import { ApiKey } from "@/types/organization";
import { Copy, Key, PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export default function ApiKeysTab() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchApiKeys();
    }
  }, [organizationId]);

  const fetchApiKeys = async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const keys = await apiKeyService.getApiKeys(organizationId);
      setApiKeys(keys);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch API keys",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!organizationId || !newKeyName.trim()) return;

    try {
      const result = await apiKeyService.createApiKey(
        organizationId,
        newKeyName.trim()
      );
      if (result) {
        setNewlyCreatedKey(result.key);
        await fetchApiKeys();
        setNewKeyName("");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create API key",
        variant: "destructive",
      });
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    try {
      const success = await apiKeyService.deleteApiKey(keyId);
      if (success) {
        toast({
          title: "Success",
          description: "API key revoked successfully",
        });
        await fetchApiKeys();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "API key copied to clipboard",
    });
  };

  if (isLoading && apiKeys.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Create and manage API keys for programmatic access to your
            organization's resources.
          </CardDescription>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Create API Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New API Key</DialogTitle>
              <DialogDescription>
                Give your API key a name to help you identify it later.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">API Key Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Production Server"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateKey} disabled={!newKeyName.trim()}>
                Create Key
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {newlyCreatedKey && (
          <Dialog
            open={!!newlyCreatedKey}
            onOpenChange={() => setNewlyCreatedKey(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>API Key Created</DialogTitle>
                <DialogDescription>
                  Your API key has been created. Make sure to copy it now as you
                  won't be able to see it again.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-muted p-3 rounded-md font-mono text-sm break-all">
                {newlyCreatedKey}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
                <Button onClick={() => setNewlyCreatedKey(null)}>Done</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {apiKeys.length === 0 ? (
          <div className="text-center py-6">
            <Key className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <h3 className="text-lg font-medium">No API Keys</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You haven't created any API keys yet.
            </p>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(true)}
              className="mx-auto"
            >
              Create your first API key
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 py-2 px-4 font-medium text-sm text-muted-foreground">
              <div>Name</div>
              <div>Created</div>
              <div className="text-right">Actions</div>
            </div>
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="grid grid-cols-3 gap-4 py-3 px-4 rounded-lg border items-center"
              >
                <div>
                  <p className="font-medium">{key.name}</p>
                  <p className="text-sm text-muted-foreground font-mono">
                    {key.key_prefix}...
                  </p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(key.created_at).toLocaleDateString()}
                </div>
                <div className="flex justify-end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to revoke this API key? This
                          action cannot be undone and any applications using
                          this key will no longer be able to access the API.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteKey(key.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Revoke Key
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
